import React, { useState, useEffect, useRef } from 'react';
import { Wifi, WifiOff, Activity, Users, RefreshCw, Play, Square, Radio, Zap, Clock, ArrowUpCircle, ArrowDownCircle, Trash2 } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

interface ServerStats {
  websocket_connections: number;
  database_connections: number;
  total_connections: number;
}

const WebsocketMonitoring: React.FC = () => {
  const { token } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [isMonitoring, setIsMonitoring] = useState(false);
  const [logs, setLogs] = useState<{ time: string; msg: string; type: 'info' | 'success' | 'error' | 'msg' | 'ping' }[]>([]);
  const [msgCount, setMsgCount] = useState(0);
  const [msgPerMin, setMsgPerMin] = useState(0);
  const [serverStats, setServerStats] = useState<ServerStats | null>(null);
  const [pingMs, setPingMs] = useState<number | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const pingTimeRef = useRef<number>(0);

  const headers = { Authorization: `Bearer ${token}` };

  const addLog = (msg: string, type: 'info' | 'success' | 'error' | 'msg' | 'ping' = 'info') => {
    const time = new Date().toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    setLogs(prev => [{ time, msg, type }, ...prev.slice(0, 199)]);
  };

  const fetchServerStats = async () => {
    try {
      const res = await axios.get('https://api.lifeshare.app/api/v1/admin/server/connections', { headers });
      setServerStats(res.data);
    } catch { /* ignore */ }
  };

  const startMonitoring = () => {
    if (!token) { addLog('Token bulunamadi', 'error'); return; }
    try {
      const ws = new WebSocket(`wss://api.lifeshare.app/ws/location?token=${token}`);
      ws.onopen = () => {
        setIsConnected(true);
        setIsMonitoring(true);
        addLog('WebSocket baglantisi kuruldu', 'success');
        fetchServerStats();
      };
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'pong') {
            const ms = Date.now() - pingTimeRef.current;
            setPingMs(ms);
            addLog(`Pong: ${ms}ms`, 'ping');
          } else {
            setMsgCount(p => p + 1);
            setMsgPerMin(p => p + 1);
            addLog(`${data.type || 'message'} ${data.user_id ? `(user:${data.user_id})` : ''}`, 'msg');
          }
        } catch {
          addLog(`Raw: ${event.data.substring(0, 80)}`, 'msg');
        }
      };
      ws.onerror = () => addLog('WebSocket hatasi', 'error');
      ws.onclose = (e) => {
        setIsConnected(false);
        setIsMonitoring(false);
        addLog(`Baglanti kapandi (code: ${e.code})`, 'error');
      };
      wsRef.current = ws;
    } catch (err) {
      addLog(`Baglanti hatasi: ${err}`, 'error');
    }
  };

  const stopMonitoring = () => {
    wsRef.current?.close();
    wsRef.current = null;
    setIsMonitoring(false);
    setIsConnected(false);
    addLog('Monitoring durduruldu', 'info');
  };

  const sendPing = () => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      pingTimeRef.current = Date.now();
      wsRef.current.send(JSON.stringify({ type: 'ping' }));
      addLog('Ping gonderildi', 'ping');
    }
  };

  useEffect(() => {
    fetchServerStats();
    const si = setInterval(fetchServerStats, 15000);
    return () => { clearInterval(si); wsRef.current?.close(); };
  }, []);

  useEffect(() => {
    const i = setInterval(() => setMsgPerMin(0), 60000);
    return () => clearInterval(i);
  }, []);

  // Auto-ping every 30s when connected
  useEffect(() => {
    if (!isConnected) return;
    const i = setInterval(sendPing, 30000);
    return () => clearInterval(i);
  }, [isConnected]);

  const logColor: Record<string, string> = {
    info: 'text-gray-400',
    success: 'text-emerald-400',
    error: 'text-red-400',
    msg: 'text-sky-400',
    ping: 'text-amber-400',
  };

  return (
    <div className="space-y-6">
      <div className="max-w-5xl mx-auto space-y-6">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-semibold text-gray-900">WebSocket</h1>
            <p className="text-sm text-gray-500 mt-1">Canli baglanti izleme</p>
          </div>
          <div className="flex gap-2">
            {isConnected && (
              <button onClick={sendPing} className="px-3 py-2.5 bg-amber-100 text-amber-700 rounded-xl text-sm font-medium hover:bg-amber-200 transition-colors flex items-center gap-1.5">
                <Radio className="w-4 h-4" /> Ping
              </button>
            )}
            {!isMonitoring ? (
              <button onClick={startMonitoring} className="px-4 py-2.5 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700 transition-colors flex items-center gap-2">
                <Play className="w-4 h-4" /> Baglan
              </button>
            ) : (
              <button onClick={stopMonitoring} className="px-4 py-2.5 bg-red-500 text-white rounded-xl font-medium hover:bg-red-600 transition-colors flex items-center gap-2">
                <Square className="w-4 h-4" /> Kes
              </button>
            )}
          </div>
        </div>

        {/* Connection Status Bar */}
        <div className={`rounded-2xl p-4 flex items-center gap-4 transition-colors ${isConnected ? 'bg-emerald-50 border border-emerald-200' : 'bg-white border border-gray-200'}`}>
          <div className={`p-2.5 rounded-xl ${isConnected ? 'bg-emerald-100' : 'bg-gray-100'}`}>
            {isConnected ? <Wifi className="w-6 h-6 text-emerald-600" /> : <WifiOff className="w-6 h-6 text-gray-400" />}
          </div>
          <div className="flex-1">
            <p className={`font-semibold ${isConnected ? 'text-emerald-800' : 'text-gray-600'}`}>{isConnected ? 'Bagli' : 'Bagli Degil'}</p>
            <p className="text-xs text-gray-400 font-mono">wss://api.lifeshare.app/ws/location</p>
          </div>
          {isConnected && (
            <div className="flex items-center gap-3">
              {pingMs !== null && (
                <span className="text-xs font-mono bg-white px-2 py-1 rounded-lg text-amber-600 border border-amber-200">{pingMs}ms</span>
              )}
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500" />
              </span>
            </div>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-white rounded-2xl p-4 border border-gray-100">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-4 h-4 text-purple-500" />
              <span className="text-xs text-gray-500">WS Baglantilar</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{serverStats?.websocket_connections ?? '-'}</p>
          </div>
          <div className="bg-white rounded-2xl p-4 border border-gray-100">
            <div className="flex items-center gap-2 mb-2">
              <Zap className="w-4 h-4 text-blue-500" />
              <span className="text-xs text-gray-500">Mesaj/dk</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{msgPerMin}</p>
          </div>
          <div className="bg-white rounded-2xl p-4 border border-gray-100">
            <div className="flex items-center gap-2 mb-2">
              <Activity className="w-4 h-4 text-green-500" />
              <span className="text-xs text-gray-500">Toplam Mesaj</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{msgCount}</p>
          </div>
          <div className="bg-white rounded-2xl p-4 border border-gray-100">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-4 h-4 text-amber-500" />
              <span className="text-xs text-gray-500">Ping</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{pingMs !== null ? `${pingMs}ms` : '-'}</p>
          </div>
        </div>

        {/* Live Logs */}
        <div className="bg-white rounded-2xl overflow-hidden border border-gray-100">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-gray-400" />
              <h2 className="font-semibold text-gray-900">Canli Loglar</h2>
              <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{logs.length}</span>
            </div>
            <button onClick={() => setLogs([])} className="text-xs text-gray-400 hover:text-red-500 transition-colors flex items-center gap-1">
              <Trash2 className="w-3.5 h-3.5" /> Temizle
            </button>
          </div>
          <div className="h-72 overflow-y-auto bg-slate-900 p-3 font-mono text-xs leading-relaxed">
            {logs.length === 0 ? (
              <p className="text-slate-500 text-center py-8">Baglaninca loglar burada gorunecek</p>
            ) : (
              logs.map((log, idx) => (
                <div key={idx} className={`flex gap-2 py-0.5 ${logColor[log.type]}`}>
                  <span className="text-slate-600 flex-shrink-0">{log.time}</span>
                  <span className="flex-shrink-0">
                    {log.type === 'success' && <ArrowUpCircle className="w-3.5 h-3.5 inline" />}
                    {log.type === 'error' && <ArrowDownCircle className="w-3.5 h-3.5 inline" />}
                    {log.type === 'msg' && <Zap className="w-3.5 h-3.5 inline" />}
                    {log.type === 'ping' && <Radio className="w-3.5 h-3.5 inline" />}
                    {log.type === 'info' && <Activity className="w-3.5 h-3.5 inline" />}
                  </span>
                  <span>{log.msg}</span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Server Connections */}
        {serverStats && (
          <div className="bg-white rounded-2xl p-5 border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">Sunucu Baglantilar</h3>
              <button onClick={fetchServerStats} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
                <RefreshCw className="w-4 h-4 text-gray-400" />
              </button>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="text-center p-3 bg-purple-50 rounded-xl">
                <p className="text-xl font-bold text-purple-700">{serverStats.websocket_connections}</p>
                <p className="text-xs text-purple-600">WebSocket</p>
              </div>
              <div className="text-center p-3 bg-blue-50 rounded-xl">
                <p className="text-xl font-bold text-blue-700">{serverStats.database_connections}</p>
                <p className="text-xs text-blue-600">Database</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-xl">
                <p className="text-xl font-bold text-gray-700">{serverStats.total_connections}</p>
                <p className="text-xs text-gray-600">Toplam</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WebsocketMonitoring;
